import { useState, useEffect } from 'react';

/**
 * Custom hook for handling rate limiting functionality
 * @param {string} pageType - The type of page (e.g., 'signin', 'signup', 'signin-email')
 * @param {number} rateLimitMinutes - Number of minutes for rate limit (default: 5)
 * @returns {Object} Rate limit state and functions
 */
export const useRateLimit = (pageType, rateLimitMinutes = 5) => {
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitEndTime, setRateLimitEndTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const storageKey = `rateLimitEndTime_${pageType}`;

  // Rate limit countdown effect
  useEffect(() => {
    let interval = null;

    if (isRateLimited && rateLimitEndTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, rateLimitEndTime - now);
        
        if (remaining <= 0) {
          setIsRateLimited(false);
          setRateLimitEndTime(null);
          setTimeRemaining(0);
          localStorage.removeItem(storageKey);
        } else {
          setTimeRemaining(Math.ceil(remaining / 1000));
        }
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRateLimited, rateLimitEndTime, storageKey]);

  // Check for existing rate limit on component mount
  useEffect(() => {
    const savedRateLimitEndTime = localStorage.getItem(storageKey);
    if (savedRateLimitEndTime) {
      const endTime = parseInt(savedRateLimitEndTime);
      const now = Date.now();
      
      if (endTime > now) {
        setIsRateLimited(true);
        setRateLimitEndTime(endTime);
        setTimeRemaining(Math.ceil((endTime - now) / 1000));
      } else {
        localStorage.removeItem(storageKey);
      }
    }
  }, [storageKey]);

  /**
   * Handle rate limit error and set the rate limit period
   * @param {Object} response - The API response object
   * @returns {boolean} - True if rate limit was applied, false otherwise
   */
  const handleRateLimitError = (response) => {
    const errorCode = response?.response?.data?.error || response?.error;
    
    if (errorCode === "RATE_LIMIT_EXCEEDED") {
      // Set rate limit for specified minutes
      const rateLimitEndTime = Date.now() + (rateLimitMinutes * 60 * 1000);
      setIsRateLimited(true);
      setRateLimitEndTime(rateLimitEndTime);
      setTimeRemaining(rateLimitMinutes * 60);
      localStorage.setItem(storageKey, rateLimitEndTime.toString());
      return true;
    }
    return false;
  };

  /**
   * Format time remaining in MM:SS format
   * @param {number} seconds - Seconds remaining
   * @returns {string} - Formatted time string
   */
  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  /**
   * Get the appropriate error message for rate limiting
   * @returns {string} - Error message
   */
  const getRateLimitMessage = () => {
    return `Too many attempts. Please try again in ${rateLimitMinutes} minutes.`;
  };

  return {
    isRateLimited,
    timeRemaining,
    handleRateLimitError,
    formatTimeRemaining,
    getRateLimitMessage
  };
};
